import React, { useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { ProposalBundle, ProposalItem, TicketCategory } from '../../api/proposalBundles';
import { formatProposalPrice } from '../../utils/priceFormatter';
import { formatTransportRouteInfo } from '../../utils/routeInfoFormatter';

interface Props {
  bundle: ProposalBundle;
  productNumber: number;
  includeTax: boolean;
  onSelect?: (bundleId: number, itemId: number) => void;
  onClose?: (bundleId: number) => void;
}

const getCategoryLabel = (category: string): string => {
  switch (category) {
    case 'outward':
      return '往路航空券';
    case 'homeward':
      return '復路航空券';
    case 'transport':
      return '交通機関';
    default:
      return '交通機関';
  }
};

const getCategoryIcon = (category: string): string => {
  switch (category) {
    case 'outward':
    case 'homeward':
    case 'transport':
      return '✈️';
    default:
      return '🚄';
  }
};

const filterItemsByTicketCategory = (items: ProposalItem[], category: TicketCategory): ProposalItem[] => {
  return items.filter(item => item.ticketCategory === category);
};

const hasItemsForCategory = (items: ProposalItem[], category: TicketCategory): boolean => {
  return items.some(item => item.ticketCategory === category);
};

const TransportCard = ({ bundle, productNumber, includeTax, onSelect, onClose }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const hasChangeable = useMemo(() => hasItemsForCategory(bundle.items, 'changeable'), [bundle.items]);
  const hasUnchangeable = useMemo(() => hasItemsForCategory(bundle.items, 'unchangeable'), [bundle.items]);

  const defaultCategory: TicketCategory = hasChangeable ? 'changeable' : 'unchangeable';
  const [selectedTicketCategory, setSelectedTicketCategory] = useState<TicketCategory>(defaultCategory);

  const filteredItems = useMemo(
    () => filterItemsByTicketCategory(bundle.items, selectedTicketCategory),
    [bundle.items, selectedTicketCategory]
  );

  const categoryLabel = getCategoryLabel(bundle.category);
  const categoryIcon = getCategoryIcon(bundle.category);
  const routeInfo = useMemo(
    () => formatTransportRouteInfo(bundle.origin, bundle.destination, bundle.departureDate),
    [bundle.origin, bundle.destination, bundle.departureDate]
  );

  const handleSelect = (itemId: number) => {
    onSelect?.(bundle.id, itemId);
  };

  const handleClose = () => {
    onClose?.(bundle.id);
  };

  const handleTicketCategoryChange = (category: TicketCategory) => {
    setSelectedTicketCategory(category);
  };

  const showTicketCategorySelector = hasChangeable && hasUnchangeable;

  return (
    <div style={styles.card}>
      <button type="button" style={styles.closeButton} onClick={handleClose}>
        ×
      </button>

      <div style={styles.header}>
        <span style={styles.productBadge}>商品{productNumber}</span>
        <span style={styles.categoryLabel}>
          {categoryIcon} {categoryLabel}
          {routeInfo}
        </span>
      </div>

      {showTicketCategorySelector && (
        <TicketCategorySelector
          bundleId={bundle.id}
          selectedCategory={selectedTicketCategory}
          onChange={handleTicketCategoryChange}
          theme={theme}
        />
      )}

      <table style={styles.table}>
        <thead>
          <tr style={styles.tableHeaderRow}>
            <th style={styles.tableHeaderCell}>時間</th>
            <th style={styles.tableHeaderCell}>便名・クラス</th>
            <th style={styles.tableHeaderCell}>料金</th>
          </tr>
        </thead>
        <tbody>
          {filteredItems.map((item, index) => (
            <TransportRow
              key={item.id}
              item={item}
              index={index}
              includeTax={includeTax}
              onSelect={() => handleSelect(item.id)}
              theme={theme}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface TicketCategorySelectorProps {
  bundleId: number;
  selectedCategory: TicketCategory;
  onChange: (category: TicketCategory) => void;
  theme: AITTheme;
}

const TicketCategorySelector: React.FC<TicketCategorySelectorProps> = ({
  bundleId,
  selectedCategory,
  onChange,
  theme
}) => {
  const styles = useMemo(() => getTicketCategorySelectorStyles(theme), [theme]);
  const radioName = `ticketCategory-${bundleId}`;

  return (
    <div style={styles.container}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label style={styles.label}>
        <input
          type="radio"
          name={radioName}
          value="changeable"
          checked={selectedCategory === 'changeable'}
          onChange={() => onChange('changeable')}
          style={styles.radio}
        />
        <span style={selectedCategory === 'changeable' ? styles.labelTextActive : styles.labelText}>
          変更可（正規運賃）
        </span>
      </label>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label style={styles.label}>
        <input
          type="radio"
          name={radioName}
          value="unchangeable"
          checked={selectedCategory === 'unchangeable'}
          onChange={() => onChange('unchangeable')}
          style={styles.radio}
        />
        <span style={selectedCategory === 'unchangeable' ? styles.labelTextActive : styles.labelText}>
          変更不可（割引運賃）
        </span>
      </label>
    </div>
  );
};

interface TransportRowProps {
  item: ProposalItem;
  index: number;
  includeTax: boolean;
  onSelect: () => void;
  theme: AITTheme;
}

const TransportRow: React.FC<TransportRowProps> = ({ item, index, includeTax, onSelect, theme }) => {
  const styles = useMemo(() => getRowStyles(theme, item.selected ?? false), [theme, item.selected]);
  const circleNumber = ['①', '②', '③', '④', '⑤'][index] || `${index + 1}`;

  const timeDisplay = item.departureTime && item.arrivalTime ? `${item.departureTime}-${item.arrivalTime}` : '-';

  const flightDisplay = item.flightName || item.title || '-';
  const seatDisplay = item.seatClass ? `（${item.seatClass}）` : '';
  const ticketBadge = item.ticketType || null;
  const priceDisplay = formatProposalPrice({
    price: item.price,
    priceWithTax: item.priceWithTax,
    includeTax
  });

  const handleClick = () => {
    if (!item.selected) {
      onSelect();
    }
  };

  return (
    <tr style={styles.row}>
      <td style={styles.cellTime}>
        <div style={styles.timeContainer}>
          <div style={styles.numberCircle}>{circleNumber}</div>
          <span>{timeDisplay}</span>
        </div>
      </td>
      <td style={styles.cell}>
        <strong>{flightDisplay}</strong>
        {seatDisplay}
        {ticketBadge && (
          <>
            <br />
            <span style={styles.ticketBadge}>{ticketBadge}</span>
          </>
        )}
      </td>
      <td style={styles.cellPrice}>
        <div style={styles.priceContainer}>
          <span>{priceDisplay}</span>
          <button type="button" style={styles.selectButton} onClick={handleClick}>
            選択
          </button>
        </div>
      </td>
    </tr>
  );
};

const getStyles = (theme: AITTheme) => ({
  card: {
    backgroundColor: theme.whiteColor,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    border: `2px solid ${theme.linkColor}`,
    position: 'relative' as const
  },
  closeButton: {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    fontWeight: 300 as const,
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  header: {
    fontWeight: 700 as const,
    color: theme.linkColor,
    marginBottom: '16px',
    fontSize: '17px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  productBadge: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const
  },
  categoryLabel: {
    fontSize: '14px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const
  },
  tableHeaderRow: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ececec'
  },
  tableHeaderCell: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px'
  },
  tableHeaderCellRight: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px'
  },
  tableHeaderCellCenter: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px',
    width: '110px'
  }
});

const getTicketCategorySelectorStyles = (theme: AITTheme) => ({
  container: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '6px'
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer'
  },
  radio: {
    width: '18px',
    height: '18px',
    accentColor: theme.linkColor,
    cursor: 'pointer'
  },
  labelText: {
    fontSize: '14px',
    color: theme.textColor
  },
  labelTextActive: {
    fontSize: '14px',
    color: theme.linkColor,
    fontWeight: 600 as const
  }
});

const getRowStyles = (theme: AITTheme, selected: boolean) => ({
  row: {
    borderBottom: '1px solid #ececec',
    backgroundColor: selected ? '#e6f4fb' : 'transparent'
  },
  cell: {
    padding: '12px 8px',
    fontSize: '13px',
    verticalAlign: 'middle' as const
  },
  cellTime: {
    padding: '12px 8px',
    fontSize: '12px',
    fontWeight: 600 as const,
    verticalAlign: 'middle' as const
  },
  timeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  cellPrice: {
    padding: '12px 8px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px',
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  numberCircle: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '12px'
  },
  ticketBadge: {
    backgroundColor: '#e6f4fb',
    color: theme.linkColor,
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px'
  },
  selectButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600 as const,
    cursor: selected ? 'default' : 'pointer',
    whiteSpace: 'nowrap' as const,
    opacity: selected ? 0.5 : 1
  }
});

export default TransportCard;
